import { X, FileText, Loader, AlertTriangle } from 'lucide-react';

interface GenerateReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isSubmitting: boolean;
  emailCount: number;
  confirmText?: string;
  /** When set, the modal shows a single-recipient confirmation instead of bulk */
  targetEmail?: string;
}

export default function GenerateReportModal({
  isOpen,
  onClose,
  onConfirm,
  isSubmitting,
  emailCount,
  confirmText,
  targetEmail,
}: GenerateReportModalProps) {
  if (!isOpen) return null;

  const isSingleTarget = Boolean(targetEmail);

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md animate-in fade-in zoom-in duration-200">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-blue-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">
              Gerar Relatório
            </h2>
          </div>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              {isSingleTarget ? (
                <>
                  <p className="text-gray-700 leading-relaxed">
                    {confirmText || 'O relatório será criado e enviado dentro de alguns minutos'} para o e-mail:
                  </p>
                  <p className="mt-2 text-sm font-semibold text-gray-900 break-all">
                    {targetEmail}
                  </p>
                </>
              ) : (
                <p className="text-gray-700 leading-relaxed">
                  {confirmText || 'Os relatórios serão criados e enviados dentro de alguns minutos'} para todos os{' '}
                  <strong className="text-gray-900">
                    {emailCount} e-mail{emailCount !== 1 ? 's' : ''}
                  </strong>{' '}
                  cadastrado{emailCount !== 1 ? 's' : ''}.
                </p>
              )}
              <p className="text-gray-600 mt-3 text-sm">
                Deseja realmente continuar?
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isSubmitting || emailCount === 0}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isSubmitting ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                <span>Gerando...</span>
              </>
            ) : (
              <span>Confirmar</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
